/**
 * Dynamic form section that renders fields based on a product template schema.
 * Used in ProductEditor for non-seed product types.
 */

import { getFieldsByGroup, getSchemaForTemplate, type TemplateFieldDef } from '@/config/template-schemas';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';

interface TemplateFieldsSectionProps {
  templateSlug: string;
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
}

function renderField(field: TemplateFieldDef, value: any, onChange: (key: string, value: any) => void) {
  switch (field.type) {
    case 'text':
      return (
        <Input
          key={field.key}
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder}
        />
      );

    case 'number':
      return (
        <Input
          key={field.key}
          label={field.unit ? `${field.label} (${field.unit})` : field.label}
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder}
        />
      );

    case 'select':
      return (
        <Select
          key={field.key}
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          options={[
            { label: `Select ${field.label.toLowerCase()}`, value: '' },
            ...(field.options || []).map((opt) => ({
              label: opt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              value: opt,
            })),
          ]}
        />
      );

    case 'multi_select': {
      const selected: string[] = Array.isArray(value) ? value : [];
      return (
        <div key={field.key}>
          <label className="block text-sm font-medium text-stone-700 mb-1">{field.label}</label>
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const next = isSelected ? selected.filter((s) => s !== opt) : [...selected, opt];
                    onChange(field.key, next);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    isSelected
                      ? 'bg-sage-600 text-white border-sage-600'
                      : 'bg-white text-stone-600 border-stone-300 hover:border-sage-400'
                  }`}
                >
                  {opt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'boolean':
      return (
        <label key={field.key} className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(field.key, e.target.checked)}
            className="w-4 h-4 rounded border-stone-300 text-sage-600 focus:ring-sage-500"
          />
          <span className="text-sm font-medium text-stone-700">{field.label}</span>
        </label>
      );

    case 'textarea':
      return (
        <Textarea
          key={field.key}
          label={field.label}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          rows={3}
        />
      );

    default:
      return null;
  }
}

export default function TemplateFieldsSection({ templateSlug, values, onChange }: TemplateFieldsSectionProps) {
  const schema = getSchemaForTemplate(templateSlug);
  const groups = getFieldsByGroup(templateSlug);

  if (schema.fields.length === 0) return null;

  return (
    <>
      {Object.entries(groups).map(([groupName, fields]) => (
        <Card key={groupName} title={groupName} padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => renderField(field, values[field.key], onChange))}
          </div>
        </Card>
      ))}
    </>
  );
}
